import { styled, Theme, CSSObject } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import { Box, Button, Card, Typography } from "@mui/material";
import { hexToRgba } from "@/Components/helpers/util";
import JiraIcon from "@/assets/icons/Jira.svg?react";
import SlackIcon from "@/assets/icons/slack-1.svg?react";
import WebhookIcon from "@/assets/icons/webhooks.svg?react";
import TeamsIcon from "@/assets/icons/microsoft-teams.svg?react";
import LinearIcon from "@/assets/icons/linear.svg?react";
import ClickUpIcon from "@/assets/icons/clickup.svg?react";
import { t } from "i18next";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import CheckIcon from "@mui/icons-material/Check";
import BoltIcon from "@mui/icons-material/Bolt";
export const drawerWidth = 240;
const RECENT_PROJECTS_KEY = "recentProject";
import HighestIcon from "@/assets/icons/highest.svg?react";
import HighIcon from "@/assets/icons/high.svg?react";
import MediumIcon from "@/assets/icons/medium.svg?react";
import LowIcon from "@/assets/icons/low.svg?react";
import SnifferIcon from "@/assets/icons/sniffer-loader.svg?react";
import AsanaIcon from "@/assets/icons/asana.svg?react";
import AzureDevOpsIcon from "@/assets/icons/azure-devops.svg?react";
import { getColorBasedOnMethod } from "@/utils/util";
import { FolderCopyOutlined, FolderOutlined, ResetTvOutlined, SpeedOutlined } from "@mui/icons-material";

export const DrawerConfig = {
  marginTop: "61px",
};

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

export const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
      },
    },
  ],
}));

export const IntegrationCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
  border: "1px solid #d6d6d6",
  boxShadow: "none",
  borderRadius: 8,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
  minHeight: "100px",
  "&:hover": {
    backgroundColor: hexToRgba(theme.palette.primary.main, 0.05),
  },
}));

export const HeaderBox = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  background: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));

export const IconGrid = styled(Box)(({ theme }) => ({
  position: "absolute",
  right: 0,
  top: 0,
  bottom: 0,
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  maxWidth: "200px",
}));

export const IconCircle = styled(Box)({
  width: 40,
  height: 40,
  borderRadius: "50%",
  backgroundColor: "white",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

export const integrations = [
  {
    id: 1,
    name: "Jira",
    description:
      "Send bugs and feedback tasks seamlessly from Issue Tracker into Jira.",
    action: "Overview",
    disabled: false,
    icon: <JiraIcon />,
    group: "Project Management",
  },
  {
    id: 2,
    name: "Asana",
    description:
      "Send bugs and feedback tasks seamlessly from Issue Tracker into Asana.",
    action: "Overview",
    disabled: true,
    icon: <AsanaIcon />,
    group: "Project Management",
  },
  {
    id: 5,
    name: "Microsoft Azure",
    description:
      "Send bugs and feedback tasks seamlessly from Issue Tracker into Azure Boards.",
    action: "Overview",
    disabled: false,
    icon: <AzureDevOpsIcon />,
    group: "Project Management",
  },
  {
    id: 3,
    name: "Microsoft Teams",
    description:
      "Send a variety of feedback and bug reports directly to the Microsoft Teams channel of your choice.",
    action: "Overview",
    disabled: true,
    icon: <TeamsIcon />,
    group: "Communication Channel",
  },
  {
    id: 4,
    name: "Slack",
    description:
      "Send a variety of feedback and bug reports directly to the Slack channel of your choice.",
    action: "Overview",
    disabled: true,
    icon: <SlackIcon />,
    group: "Communication Channel",
  },
  // {
  //   id: 4,
  //   name: "Sniffer CDN",
  //   description:
  //     "Send a variety of feedback and bug reports directly to the Sniffer auto bug creation platform.",
  //   action: "Read Documentation",
  //   disabled: false,
  //   isConnect: true,
  //   icon: <SnifferIcon />,
  // },
  // {
  //   id: 5,
  //   name: "Webhooks",
  //   description: "Create your own bespoke integration using webhooks.",
  //   action: "Overview",
  //   disabled: true,
  //   icon: <WebhookIcon />,
  // },
  
  // {
  //   id: 5,
  //   name: "Linear",
  //   description:
  //     "Send bugs and website feedback reported in Bug Report directly to Linear.",
  //   action: "Overview",
  //   disabled: true,
  //   icon: <LinearIcon />,
  // },
  // {
  //   id: 6,
  //   name: "ClickUp",
  //   description:
  //     "Track bugs and report website feedback in ClickUp by integrating with Bug Report.",
  //   action: "Overview",
  //   disabled: true,
  //   icon: <ClickUpIcon />,
  // },
];

export const ModalHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: "20px 24px",
  borderBottom: "1px solid #e0e0e0",
  backgroundColor: theme.palette.primary.main,
  borderRadius: "8px 8px 0 0",
}));

export const platformOptions = [
  { value: "NONE", label: "None" },
  { value: "JIRA", label: "Jira" },
  { value: "FRESHDESK", label: "Freshdesk" },
];

export const DeleteButton = styled(Button)(({ theme }) => ({
  color: "#f44336",
  borderColor: "#f44336",
  textTransform: "none",
  borderRadius: "20px",
  padding: "6px 16px",
  "&:hover": {
    backgroundColor: "rgba(244, 67, 54, 0.04)",
    borderColor: "#d32f2f",
  },
}));

export const colorPalette = [
  "#00A3BF",
  "#172B4D",
  "#00875A",
  "#0052CC",
  "#E52B50",
  "#26A69A",
  "#FF991F",
  "#5243AA",
  "#FF8C00",
  "#9C27B0",
  "#1E88E5",
  "#00796B",
  "#7B1FA2",
  "#5D4037",
  "#2196F3",
  "#FF5722",
  "#673AB7",
];

export const getDraftSprints = (sprints: any[]) => {
  return sprints.filter((sprint: any) => sprint.status === "DRAFT");
};

export const getActiveSprints = (sprints: any[]) => {
  return sprints.filter((sprint: any) => sprint.status === "ACTIVE");
};

/**
 * Creates a lighter shade of a given color
 * @param {string} color - Hex color code (e.g., "#FF0000")
 * @param {number} factor - Lightening factor between 0 and 1 (0.3 = 30% lighter)
 * @returns {string} - Lighter hex color code
 */
export const getLighterShade = (color, factor = 0.4) => {
  // Remove the hash if present
  const hex = color.replace("#", "");

  // Parse the hex color into RGB components
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  // Calculate lighter values by moving towards white (255)
  const lighterR = Math.round(r + (255 - r) * factor);
  const lighterG = Math.round(g + (255 - g) * factor);
  const lighterB = Math.round(b + (255 - b) * factor);

  // Convert back to hex and ensure 2 digits
  const toHex = (value) => {
    const hex = value.toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };

  return `#${toHex(lighterR)}${toHex(lighterG)}${toHex(lighterB)}`;
};

export const getInitials = (fullName: string) => {
  const nameParts = fullName.split(" ");
  if (nameParts.length === 1) {
    return nameParts?.[0].substring(0, 2).toUpperCase();
  }
  const initials = nameParts
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
  return initials?.length > 2 ? initials?.substring(0, 2) : initials;
};

export const typeToIconMapping = {
  BUG: {
    bgcolor: "#E5493A",
    icon: <FiberManualRecordIcon sx={{ fontSize: 10, color: "white" }} />,
  },
  STORY: {
    bgcolor: "#63BA3C",
    icon: <BookmarkIcon sx={{ fontSize: 9, color: "white" }} />,
  },
  TASK: {
    bgcolor: "#4BADE8",
    icon: <CheckIcon sx={{ fontSize: 10, color: "white" }} />,
  },
  EPIC: {
    bgcolor: "#904EE2",
    icon: <BoltIcon sx={{ fontSize: 10, color: "white" }} />,
  },
  DEFECT: {
    bgcolor: "#E5493A",
    icon: <FiberManualRecordIcon sx={{ fontSize: 10, color: "white" }} />,
  },
  SUGGESTIONS: {
    bgcolor: "#F6C445",
    icon: <LightbulbOutlinedIcon sx={{ fontSize: 10, color: "white" }} />,
  },
};

export const severityMap = {
  Highest: "Highest Priority",
  High: "High Priority",
  Medium: "Medium Priority",
  Low: "Low Priority",
};

export const severityColorMap = {
  Highest: "#D91656",
  High: "#EB5B00",
  Medium: "#FFB200",
  Low: "#003092",
};

export const severityOptions = [
  { value: "Highest", label: "Highest", icon: <HighestIcon /> },
  { value: "High", label: "High", icon: <HighIcon /> },
  { value: "Medium", label: "Medium", icon: <MediumIcon /> },
  { value: "Low", label: "Low", icon: <LowIcon /> },
];

export const getSeverityIcon = (severity) => {
  const style = {
    minWidth: "auto",
    minHeight: "auto",
    width: 20,
    height: 20,
  };
  switch (severity) {
    case "Highest":
      return <HighestIcon style={style} />;
    case "High":
      return <HighIcon style={style} />;
    case "Medium":
      return <MediumIcon style={style} />;
    case "Low":
      return <LowIcon style={style} />;
    default:
      return null;
  }
};

export type RelationshipType =
  | "is blocked by"
  | "blocks"
  | "is cloned by"
  | "clones"
  | "is duplicated by"
  | "duplicates"
  | "relates to";

export const relationshipTypes: RelationshipType[] = [
  "is blocked by",
  "blocks",
  "is cloned by",
  "clones",
  "is duplicated by",
  "duplicates",
  "relates to",
];

export const getStatusColor = (status: string) => {
  const statusLower = status?.toLowerCase();
  if (statusLower?.includes("done") || statusLower?.includes("closed")) {
    return "success";
  } else if (
    statusLower?.includes("progress") ||
    statusLower?.includes("active")
  ) {
    return "primary";
  } else if (statusLower?.includes("review")) {
    return "warning";
  }
  return "default";
};

export const getTypeIcon = (type: string) => {
  const typeInfo = typeToIconMapping[type as keyof typeof typeToIconMapping];
  if (!typeInfo) return null;
  return (
    <Box
      sx={{
        width: 16,
        height: 16,
        borderRadius: "3px",
        backgroundColor: typeInfo.bgcolor,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {typeInfo.icon}
    </Box>
  );
};

export const getProjectIdForDetails = (bugDetailData: any) => {
  if (
    typeof bugDetailData?.project === "object" &&
    bugDetailData?.project?._id
  ) {
    return bugDetailData?.project._id;
  }
  if (typeof bugDetailData?.project === "string") {
    return bugDetailData?.project;
  }
  return null;
};

// Available colors for sticky notes
export const noteColors = [
  "#FFE066", // Yellow
  "#FF9999", // Pink
  "#99CCFF", // Blue
  "#99FF99", // Green
  "#FFB366", // Orange
  "#CC99FF", // Purple
  "#66FFCC", // Mint
  "#FFCC99", // Peach
];

// Available emojis
export const availableEmojis = [
  "👍",
  "👎",
  "👏",
  "🎉",
  "💡",
  "❤️",
  "😊",
  "😢",
  "😠",
  "🤔",
  "⭐",
  "🔥",
  "✅",
  "❌",
  "🚀",
  "⚠️",
  "📚",
  "💭",
  "🎯",
  "🏆",
  "🤝",
  "💪",
  "🧠",
  "⚡",
];

export const getHashCode = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
  }
  return hash;
};

export const getBackgroundColor = (userId, projectId?: string) => {
  try {
    if (!userId || typeof userId !== "string") {
      throw new Error("Invalid userId");
    }

    const key =
      typeof projectId === "string" && projectId.length > 0
        ? `${projectId}:${userId}`
        : userId;
    const hashCode = getHashCode(key);
    const colorIndex = Math?.abs(hashCode) % colorPalette.length;
    return colorPalette[colorIndex];
  } catch (error) {
    return "#CCCCCC";
  }
};
/**
 * Strips HTML tags from a string and returns plain text
 * @param {string} html - The HTML string to strip tags from
 * @returns {string} - Plain text without HTML tags
 */
export const stripHtmlTags = (html: string) => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
};

export const getDateRange = (
  option: string,
): { startDate: string; endDate: string } => {
  const today = new Date();
  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  switch (option) {
    case "no_date":
      return {
        startDate: "no-date",
        endDate: "no-date",
      };

    case "today":
      return {
        startDate: formatDate(today),
        endDate: formatDate(today),
      };

    case "yesterday":
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      return {
        startDate: formatDate(yesterday),
        endDate: formatDate(yesterday),
      };

    case "tomorrow":
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);
      return {
        startDate: formatDate(tomorrow),
        endDate: formatDate(tomorrow),
      };

    case "this_week":
      const startOfWeek = new Date(today);
      const dayOfWeek = today.getDay();
      const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
      startOfWeek.setDate(diff);

      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);

      return {
        startDate: formatDate(startOfWeek),
        endDate: formatDate(endOfWeek),
      };

    case "last_7_days":
      const last7Start = new Date(today);
      last7Start.setDate(today.getDate() - 7);
      return {
        startDate: formatDate(last7Start),
        endDate: formatDate(today),
      };

    case "next_7_days":
      const next7End = new Date(today);
      next7End.setDate(today.getDate() + 7);
      return {
        startDate: formatDate(today),
        endDate: formatDate(next7End),
      };

    case "next_week":
      const nextWeekStart = new Date(today);
      const currentDayOfWeek = today.getDay();
      const daysUntilNextMonday =
        currentDayOfWeek === 0 ? 1 : 8 - currentDayOfWeek;
      nextWeekStart.setDate(today.getDate() + daysUntilNextMonday);

      const nextWeekEnd = new Date(nextWeekStart);
      nextWeekEnd.setDate(nextWeekStart.getDate() + 6);

      return {
        startDate: formatDate(nextWeekStart),
        endDate: formatDate(nextWeekEnd),
      };

    default:
      return {
        startDate: "",
        endDate: "",
      };
  }
};
export const getRecentProjects = (): { _id: string; name: string; projectKey?: string; icon?: string }[] => {
  if (typeof window !== "undefined") {
    const recent = localStorage.getItem(RECENT_PROJECTS_KEY);
    return recent ? JSON.parse(recent) : [];
  }
  return [];
};
export const addRecentProject = (project: any) => {
  if (typeof window !== "undefined") {
    let recent = getRecentProjects();

    // Remove if already exists
    recent = recent.filter((item) => item._id !== project?._id);

    // Add to beginning
    recent.unshift({
      _id: project?._id,
      name: project?.name,
      projectKey: project?.projectKey,
      icon: project?.icon,
      boardType: project?.boardType,
    });

    // Keep only the last 5 projects
    recent = recent.slice(0, 5);

    localStorage.setItem(RECENT_PROJECTS_KEY, JSON.stringify(recent));
  }
};

export const getFilterValue = (
  groupId: string,
  groupData: any,
  isMyTaskView: boolean = false,
  statusList: any = null,
) => {
  switch (groupData.type) {
    case "status":
      if (isMyTaskView) {
        // For My Tasks view, find the status object and use its statusIds array
        const statusObject = statusList?.find(
          (status: any) => status._id === groupId,
        );
        if (
          statusObject &&
          statusObject.statusIds &&
          statusObject.statusIds.length > 0
        ) {
          return { statuses: statusObject.statusIds };
        }
        // Fallback to single status ID if statusIds array is not available
        return { statuses: [groupId] };
      } else {
        // For regular project view, pass status ID as before
        return { statuses: [groupId] };
      }

    case "assignee":
      return { assignTo: [groupId] };

    case "priority":
      return { severities: [groupData.title] };

    case "tag":
      return { tags: [groupData.tagName] };

    case "taskType":
      return { type: groupData.title };

    case "dueDate":
      return getDueDateFilter(groupId);

    case "startDate": // Add this case
      return getStartDateFilter(groupId);

    default:
      return {};
  }
};

// Update getDueDateFilter function
const getDueDateFilter = (groupId: string) => {
  const today = new Date();
  const startOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const endOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
    23,
    59,
    59,
  );

  switch (groupId) {
    case "overdue":
      return { dueDateEnd: startOfToday.toISOString() };

    case "today":
      return {
        dueDateStart: startOfToday.toISOString(),
        dueDateEnd: endOfToday.toISOString(),
      };

    case "this_week":
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay() + 1);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      return {
        dueDateStart: startOfWeek.toISOString(),
        dueDateEnd: endOfWeek.toISOString(),
      };

    case "next_week":
      const nextWeekStart = new Date(today);
      nextWeekStart.setDate(today.getDate() + 7 - today.getDay());
      const nextWeekEnd = new Date(nextWeekStart);
      nextWeekEnd.setDate(nextWeekStart.getDate() + 6);
      return {
        dueDateStart: nextWeekStart.toISOString(),
        dueDateEnd: nextWeekEnd.toISOString(),
      };

    case "later":
      const twoWeeksFromNow = new Date(today);
      twoWeeksFromNow.setDate(today.getDate() + 14);
      return { dueDateStart: twoWeeksFromNow.toISOString() };

    case "no_due_date":
      return {
        dueDateStart: "no-date",
        dueDateEnd: "no-date",
      };

    default:
      return {};
  }
};

// Update getStartDateFilter function
const getStartDateFilter = (groupId: string) => {
  const today = new Date();
  const startOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const endOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
    23,
    59,
    59,
  );

  switch (groupId) {
    case "overdue_start":
      return { startDateEnd: startOfToday.toISOString() };

    case "today_start":
      return {
        startDateStart: startOfToday.toISOString(),
        startDateEnd: endOfToday.toISOString(),
      };

    case "this_week_start":
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay() + 1);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      return {
        startDateStart: startOfWeek.toISOString(),
        startDateEnd: endOfWeek.toISOString(),
      };

    case "next_week_start":
      const nextWeekStart = new Date(today);
      nextWeekStart.setDate(today.getDate() + 7 - today.getDay());
      const nextWeekEnd = new Date(nextWeekStart);
      nextWeekEnd.setDate(nextWeekStart.getDate() + 6);
      return {
        startDateStart: nextWeekStart.toISOString(),
        startDateEnd: nextWeekEnd.toISOString(),
      };

    case "later_start":
      const twoWeeksFromNow = new Date(today);
      twoWeeksFromNow.setDate(today.getDate() + 14);
      return { startDateStart: twoWeeksFromNow.toISOString() };

    case "no_start_date":
      return {
        startDateStart: "no-date",
        startDateEnd: "no-date",
      }; // Both have "no-date"

    default:
      return {};
  }
};

// Usage examples:
// For main groups
export const getFilterValueForGroup = (
  groupId: string,
  groupData: any,
  isMyTaskView: boolean,
  statusList: any,
) => {
  return getFilterValue(groupId, groupData, isMyTaskView, statusList);
};

// For sub-groups
export const getFilterValueForSubGroup = (
  subGroupId: string,
  subGroupData: any,
  isMyTaskView: boolean,
  statusList: any,
) => {
  return getFilterValue(subGroupId, subGroupData, isMyTaskView, statusList);
};

// Export the helper functions for external use if needed
export { getDueDateFilter, getStartDateFilter };

export const getSystemInfo = async () => {
  const userAgent = navigator.userAgent;
  const platform = navigator.platform;

  // Operating System Detection
  const getOperatingSystemInfo = () => {
    let osName = "Unknown";
    let osVersion = "Unknown";

    if (userAgent.includes("Windows NT")) {
      osName = "Windows";
      const match = userAgent.match(/Windows NT (\d+\.\d+)/);
      if (match) {
        const version = match[1];
        const windowsVersions = {
          "10.0": "10/11",
          "6.3": "8.1",
          "6.2": "8",
          "6.1": "7",
          "6.0": "Vista",
          "5.1": "XP",
          "5.0": "2000",
        };
        osVersion = windowsVersions[version] || version;
      }
    } else if (userAgent.includes("Mac OS X")) {
      osName = "macOS";
      const match = userAgent.match(/Mac OS X (\d+[._]\d+[._]?\d*)/);
      if (match) {
        osVersion = match[1].replace(/_/g, ".");
      }
    } else if (userAgent.includes("Linux")) {
      osName = "Linux";
      if (userAgent.includes("Ubuntu")) osName = "Ubuntu";
      else if (userAgent.includes("Fedora")) osName = "Fedora";
      else if (userAgent.includes("SUSE")) osName = "SUSE";
      else if (userAgent.includes("Red Hat")) osName = "Red Hat";
    } else if (userAgent.includes("Android")) {
      osName = "Android";
      const match = userAgent.match(/Android (\d+[.\d]*)/);
      if (match) {
        osVersion = match[1];
      }
    } else if (userAgent.includes("iPhone") || userAgent.includes("iPad")) {
      osName = userAgent.includes("iPhone") ? "iOS" : "iPadOS";
      const match = userAgent.match(/OS (\d+[._]\d+[._]?\d*)/);
      if (match) {
        osVersion = match[1].replace(/_/g, ".");
      }
    } else if (userAgent.includes("CrOS")) {
      osName = "Chrome OS";
      const match = userAgent.match(/CrOS \w+ (\d+[.\d]*)/);
      if (match) {
        osVersion = match[1];
      }
    }

    return `${osName} ${osVersion}`.trim();
  };

  // Browser Detection
  const getBrowserInfo = () => {
    let browserName = "Unknown";
    let browserVersion = "Unknown";
    let engine = "Unknown";

    if (userAgent.includes("Chrome") && !userAgent.includes("Edg")) {
      browserName = "Chrome";
      engine = "Blink";
      const match = userAgent.match(/Chrome\/(\d+[\.\d]*)/);
      if (match) browserVersion = match[1];
    } else if (userAgent.includes("Edg")) {
      browserName = "Microsoft Edge";
      engine = "Blink";
      const match = userAgent.match(/Edg\/(\d+[\.\d]*)/);
      if (match) browserVersion = match[1];
    } else if (userAgent.includes("Firefox")) {
      browserName = "Firefox";
      engine = "Gecko";
      const match = userAgent.match(/Firefox\/(\d+[\.\d]*)/);
      if (match) browserVersion = match[1];
    } else if (userAgent.includes("Safari") && !userAgent.includes("Chrome")) {
      browserName = "Safari";
      engine = "WebKit";
      const match = userAgent.match(/Version\/(\d+[\.\d]*)/);
      if (match) browserVersion = match[1];
    } else if (userAgent.includes("OPR")) {
      browserName = "Opera";
      engine = "Blink";
      const match = userAgent.match(/OPR\/(\d+[\.\d]*)/);
      if (match) browserVersion = match[1];
    } else if (userAgent.includes("Trident")) {
      browserName = "Internet Explorer";
      engine = "Trident";
      const match = userAgent.match(/rv:(\d+[\.\d]*)/);
      if (match) browserVersion = match[1];
    }

    return `${browserName} ${browserVersion}`.trim();
  };

  // Screen Information
  const getScreenInfo = () => {
    return `${screen.width}x${screen.height}`;
  };

  // Hardware Information
  const getHardwareInfo = () => {
    return {
      cpuCores: navigator.hardwareConcurrency || "Unknown",
      memory: navigator.deviceMemory
        ? `${navigator.deviceMemory}GB`
        : "Unknown",
      platform: navigator.platform,
      architecture: navigator.userAgentData?.platform || "Unknown",
      cookieEnabled: navigator.cookieEnabled,
      colorDepth: screen.colorDepth || "Unknown",
    };
  };

  // Language and Locale Information
  const getLanguageInfo = () => {
    return {
      primary: navigator.language,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  };

  const systemInfo = {
    operatingSystem: getOperatingSystemInfo(),
    browser: getBrowserInfo(),
    screen: getScreenInfo(),
    hardware: getHardwareInfo(),
    language: getLanguageInfo(),
    url: window.location.href,
  };

  return systemInfo;
};

export const getOperatingSystemInfo = async () => {
  const systemInfo = await getSystemInfo();
  return systemInfo;
};

// Route mapping utilities for sidebar navigation
export const getSelectedItemFromPath = (pathname: string): string => {
  // Check specific paths first (more specific patterns before less specific)
  if (pathname.includes("/settings/members")) return "Members";
  if (pathname.includes("/settings/tags")) return "Tags";
  if (pathname.includes("/settings/integrations")) return "Integrations";
  if (pathname.includes("/settings/appearance")) return "Appearance";
  if (pathname.includes("/settings/sniffer-autobug")) return "Live Logs Docs";
  if (pathname.includes("/settings")) return "Project Settings";
  if (pathname.includes("/recurring-tasks")) return "Recurring Tasks";
  if (pathname.includes("/retrospectives")) return "Retrospectives";
  if (pathname.includes("/system-bugs")) return "Live Logs";
  if (pathname.includes("/my-tasks")) return "My Tasks";
  if (pathname.includes("/dashboard")) return "Dashboard";
  if (pathname.includes("/feedback-config")) return "Feedback Config";
  if (pathname.includes("/feedback")) return "Feedback";
  if (pathname.includes("/backlog")) return "Backlog";
  if (pathname.includes("/task-board")) return "Task Board";
  if (pathname.includes("/applications")) return "Applications";
  if (pathname.includes("/sniffer-autobug")) return "Live Logs Docs";
  // Check for specific sprint (has sprint ID after /sprints/)
  const sprintMatch = pathname.match(/\/sprints\/([^/]+)/);
  if (sprintMatch) return `Sprint:${sprintMatch[1]}`; // Returns Sprint:sprintId
  if (pathname.includes("/sprints")) return "All Sprints";
  return "Dashboard";
};

export const getSprintIdFromPath = (pathname: string): string | null => {
  const sprintMatch = pathname.match(/\/sprints\/([^/]+)/);
  return sprintMatch ? sprintMatch[1] : null;
};

export const getPathFromSelectedItem = (
  item: string,
  projectId: string,
  sprintId?: string,
): string => {
  const base = `/defect-tracker/projects/${projectId}`;

  switch (item) {
    case "Dashboard":
      return `${base}/dashboard`;
    case "Feedback":
      return `${base}/feedback`;
    case "Feedback Config":
      return `${base}/feedback-config`;
    case "Backlog":
      return `${base}/backlog`;
    case "Task Board":
      return `${base}/task-board`;
    case "Applications":
      return `${base}/applications`;
    case "All Sprints":
      return `${base}/sprints`;
    case "Project Settings":
      return `${base}/settings`;
    case "Members":
      return `${base}/settings/members`;
    case "Tags":
      return `${base}/settings/tags`;
    case "Integrations":
      return `${base}/settings/integrations`;
    case "Appearance":
      return `${base}/settings/appearance`;
    case "Live Logs Docs":
      return `${base}/settings/sniffer-autobug`;
    case "Recurring Tasks":
      return `${base}/recurring-tasks`;
    case "Retrospectives":
      return `${base}/retrospectives`;
    case "Live Logs":
      return `${base}/system-bugs`;
    case "My Tasks":
      return "/defect-tracker/my-tasks";
    default:
      // For sprint items - check if sprintId is provided or extract from item name
      if (sprintId) {
        return `${base}/sprints/${sprintId}`;
      }
      // If item starts with "Sprint:" extract the ID
      if (item.startsWith("Sprint:")) {
        const id = item.replace("Sprint:", "");
        return `${base}/sprints/${id}`;
      }
      return `${base}/dashboard`;
  }
};

// Get view type based on route
export const getViewTypeFromPath = (pathname: string): string => {
  if (pathname.includes("/dashboard")) return "dashboard";
  if (pathname.includes("/feedback-config")) return "feedback-config";
  if (pathname.includes("/feedback")) return "feedback";
  if (pathname.includes("/backlog")) return "backlog";
  if (pathname.includes("/task-board")) return "kanban";
  if (pathname.includes("/sprints")) return "kanban";
  return "dashboard";
};

// Get backlog view type based on route
export const getBacklogViewTypeFromPath = (pathname: string): string => {
  if (pathname.includes("/backlog")) return "backlog";
  if (pathname.includes("/sprints") && !pathname.match(/\/sprints\/[^/]+/))
    return "allSprint";
  if (pathname.includes("/sprints")) return "kanban";
  if (pathname.includes("/task-board")) return "kanban";
  return "table";
};

export const renderMethodOrType = (method: string, short?: boolean) => {
	switch (method) {
		case 'PERFORMANCE_TEST':
			return (
				<Box
					sx={{
						pt: 0.2,
					}}
				>
					<SpeedOutlined sx={{ color: 'primary.main' }} />
				</Box>
			);
		case 'GET':
			return (
				<Box
					style={{
						color: getColorBasedOnMethod(method),
						fontWeight: 'bold',
						textTransform: 'uppercase',
					}}
				>
					{short ? 'GET' : 'GET'}
				</Box>
			);
		case 'POST':
			return (
				<Box
					style={{
						color: getColorBasedOnMethod(method),
						fontWeight: 'bold',
						textTransform: 'uppercase',
					}}
				>
					{short ? 'POST' : 'POST'}
				</Box>
			);
		case 'PUT':
			return (
				<Box
					style={{
						color: getColorBasedOnMethod(method),
						fontWeight: 'bold',
						textTransform: 'uppercase',
					}}
				>
					{short ? 'PUT' : 'PUT'}
				</Box>
			);
		case 'DELETE':
			return (
				<Box
					style={{
						color: getColorBasedOnMethod(method),
						fontWeight: 'bold',
						textTransform: 'uppercase',
					}}
				>
					{short ? 'DEL' : 'DELETE'}
				</Box>
			);
		case 'PATCH':
			return (
				<Box
					style={{
						color: getColorBasedOnMethod(method),
						fontWeight: 'bold',
						textTransform: 'uppercase',
					}}
				>
					{short ? 'PATCH' : 'PATCH'}
				</Box>
			);
		case 'OPTIONS':
			return (
				<Box
					style={{
						color: getColorBasedOnMethod(method),
						fontWeight: 'bold',
						textTransform: 'uppercase',
					}}
				>
					{short ? 'OPT' : 'OPTIONS'}
				</Box>
			);
		case 'HEAD':
			return (
				<Box
					style={{
						color: getColorBasedOnMethod(method),
						fontWeight: 'bold',
						textTransform: 'uppercase',
					}}
				>
					{short ? 'HEAD' : 'HEAD'}
				</Box>
			);
		case 'TEST_SUITE':
			return (
				<Box
					bgcolor={(theme) => theme.palette.primary.main}
					color="#fff"
					borderRadius="15%"
					width={25}
					height="100%"
					display="flex"
					alignItems="center"
					justifyContent="center"
					mr={0.4}
					pt={0.2}
				>
					<Typography fontSize="11px" fontWeight={600}>
						TS
					</Typography>
				</Box>
			);
		case 'SHOW_TEST_ICON':
			// return <SettingsInputComponentIcon />;
			return (
				<Box
					bgcolor={(theme) => hexToRgba(theme.palette.primary.main, 0.7)}
					color="#fff"
					borderRadius="15%"
					width={25}
					height={16.5}
					display="flex"
					alignItems="center"
					justifyContent="center"
					mr={0.4}
					pt={0.2}
					flexShrink={0}
				>
					<Typography fontSize="11px" fontWeight={600}>
						TC
					</Typography>
				</Box>
			);
		case 'EXAMPLE':
			return <Box>Ex.</Box>;
		case 'GRAPHQL':
			return (
				<Box
					component="img"
					src="/images/GraphQL.png"
					alt="GraphQL"
					sx={{
						width: 16,
						height: 16,
						display: 'inline-block',
					}}
				/>
			);
		case 'GRPC':
			return (
				<Box
					component="img"
					src="/images/grpc.png"
					alt="GRPC"
					sx={{
						width: 14,
						height: 16,
						display: 'inline-block',
					}}
				/>
			);
		case 'MCP':
			return (
				<Box
					component="img"
					src="/images/mcp.png"
					alt="MCP"
					sx={{
						width: 14,
						height: 16,
						display: 'inline-block',
					}}
				/>
			);
		case 'FOLDER':
			return <FolderOutlined />;
		case 'COLLECTION':
			return <FolderCopyOutlined sx={{ fontSize: '18px' }} />;

		case 'MONITOR':
			return <ResetTvOutlined sx={{ color: '#9aa2b1' }} />;
		default:
			return (
				<Box
					style={{
						color: getColorBasedOnMethod(method),
						fontWeight: 'bold',
						textTransform: 'uppercase',
					}}
				>
					{method}
				</Box>
			);
	}
};
